import { debounce, deepEqual } from '@deckee/utilities';

import { telemetry } from '../backend';
import { get, getStatic, set } from '../local-storage';
import { getStore } from '../store';

// Custom Layers
import ais from './custom-layers/ais';
import dynamicActivityHeatmap from './custom-layers/dynamic-activity-heatmap';
import navigation from './custom-layers/navigation';
import tripPlan from './custom-layers/trip-plan';
import bathymetry from './custom-layers/bathymetry';

// Line Layers
import loadedTrips from './line-layers/loaded-trips';
import logTrips from './line-layers/log-trips';

// Image Layers
import currentLocationMarker from './image-layers/current-location-markers';
import marketplaceMarkers from './image-layers/marketplace-markers';
import signalMarkers from './image-layers/signal-markers';
import storyMarker from './image-layers/story-markers';
import tempLocationMarker from './image-layers/temp-location-markers';
import eventMarker from './image-layers/event-marker';
import boatUsMarker from './image-layers/boatus-marker';
import partnerMarker from './image-layers/partner-marker';

// Static Data Layers
import activityArea from './static-data-layers/activity-area';
import environmentArea from './static-data-layers/environment-area';
import restrictedArea from './static-data-layers/restricted-area';
import shipwreck from './static-data-layers/shipwreck';
import speedLimitationArea from './static-data-layers/speed-limitation-area';

// Deckee Markers, all stored on our backend
import { hasFeatureEnabled } from '../feature';
import {
  getSelectedLayers,
  updateSelectedMapLayers,
} from '../store/modules/map-settings';
import anchorageMarker from './marker-layers/anchorageMarker';
import barCrossingMarker from './marker-layers/barCrossingMarker';
import boatRampMarker from './marker-layers/boatRampMarker';
import boatStorageMarker from './marker-layers/boatStorageMarker';
import bridgeMarker from './marker-layers/bridgeMarker';
import chandleryMarker from './marker-layers/chandleryMarker';
import customsAndImmigrationMarker from './marker-layers/customsAndImmigrationMarker';
import divingSpotMarker from './marker-layers/divingSpotMarker';
import electricalStoreMarker from './marker-layers/electricalStoreMarker';
import fishingLocationMarker from './marker-layers/fishingLocationMarker';
import fuelDockMarker from './marker-layers/fuelDockMarker';
import fuelStationMarker from './marker-layers/fuelStationMarker';
import generalInformationMarker from './marker-layers/generalInformationMarker';
import groceryStoreMarker from './marker-layers/groceryStoreMarker';
import hardwareStoreMarker from './marker-layers/hardwareStoreMarker';
import hauloutFacilityMarker from './marker-layers/hauloutFacilityMarker';
import incidentMarker from './marker-layers/incidentMarker';
import lighthouseMarker from './marker-layers/lighthouseMarker';
import marinaMarker from './marker-layers/marinaMarker';
import marineInformationMarker from './marker-layers/marineInformationMarker';
import marineLifeMarker from './marker-layers/marineLifeMarker';
import marpMarker from './marker-layers/marpMarker';
import mooringMarker from './marker-layers/mooringMarker';
import navigationInformationMarker from './marker-layers/navigationInformationMarker';
import needHelpMarker from './marker-layers/needHelpMarker';
import overheadCableMarker from './marker-layers/overheadCableMarker';
import plumbingStoreMarker from './marker-layers/plumbingStoreMarker';
import policeMarker from './marker-layers/policeMarker';
import portOverviewMarker from './marker-layers/portOverviewMarker';
import pumpOutMarker from './marker-layers/pumpOutMarker';
import showerMarker from './marker-layers/showerMarker';
import snorkelingSpotMarker from './marker-layers/snorkelingSpotMarker';
import toiletsMarker from './marker-layers/toiletsMarker';
import unsafeAnchorageMarker from './marker-layers/unsafeAnchorageMarker';
import warningMarker from './marker-layers/warningMarker';
import webcamMarker from './marker-layers/webcamMarker';
import wharfMarker from './marker-layers/wharfMarker';
import fadMarker from './marker-layers/fadMarkerLayer';

// All of these markers were setup before we were storing markers as an object. It's important
// to keep this list so that we can upgrade user layers to the new format
// DO NOT MODIFY, we will not need to remove or add anything other items to this list
export const OLD_MARKERS = [
  'activityArea',
  'ais',
  'anchorageMarker',
  'barCrossingMarker',
  'boatRampMarker',
  'boatStorageMarker',
  'bridgeMarker',
  'chandleryMarker',
  'currentLocationMarker',
  'customsAndImmigrationMarker',
  'divingSpotMarker',
  'dynamicActivityHeatmap',
  'electricalStoreMarker',
  'environmentArea',
  'fishingLocationMarker',
  'fuelDockMarker',
  'fuelStationMarker',
  'generalInformationMarker',
  'groceryStoreMarker',
  'hardwareStoreMarker',
  'hauloutFacilityMarker',
  'incidentMarker',
  'lighthouseMarker',
  'loadedTrips',
  'logTrips',
  'marinaMarker',
  'marineInformationMarker',
  'marineLifeMarker',
  'marketplaceMarkers',
  'marpMarker',
  'mooringMarker',
  'navigation',
  'navigationInformationMarker',
  'needHelpMarker',
  'overheadCableMarker',
  'plumbingStoreMarker',
  'policeMarker',
  'portOverviewMarker',
  'pumpOutMarker',
  'restrictedArea',
  'shipwreck',
  'showerMarker',
  'snorkelingSpotMarker',
  'speedLimitationArea',
  'storyMarker',
  'tempLocationMarker',
  'toiletsMarker',
  'tripPlan',
  'unsafeAnchorageMarker',
  'warningMarker',
  'webcamMarker',
  'wharfMarker',
];

// Not sure the following two helper methods belong on this manager...
export { layerExists } from './_helpers';

const isSelectedLayersInOldFormat = () => {
  const selectedLayers = getStatic('mapLayerService', 'selectedMapLayers');
  if (!selectedLayers) {
    return false;
  }
  return Array.isArray(selectedLayers);
};

export const getLayerDefinitions = () => {
  const configuration = {
    // Custom Layers
    ais,
    navigation,
    tripPlan,
    wmsbathymetry: bathymetry,
    dynamicActivityHeatmap,
    eventMarker,
    boatUsMarker,
    partnerMarker,

    // Line Layers
    logTrips,
    loadedTrips,

    // Image Layers
    storyMarker,

    // Static Data Layers
    activityArea,
    restrictedArea,
    speedLimitationArea,
    environmentArea,
    shipwreck,

    // Deckee Markers, all stored on our backend
    [anchorageMarker.standardMarkerOptions.markerType]: anchorageMarker,
    [barCrossingMarker.standardMarkerOptions.markerType]: barCrossingMarker,
    [boatRampMarker.standardMarkerOptions.markerType]: boatRampMarker,
    [fadMarker.standardMarkerOptions.markerType]: fadMarker,
    [boatStorageMarker.standardMarkerOptions.markerType]: boatStorageMarker,
    [bridgeMarker.standardMarkerOptions.markerType]: bridgeMarker,
    [chandleryMarker.standardMarkerOptions.markerType]: chandleryMarker,
    [customsAndImmigrationMarker.standardMarkerOptions.markerType]:
      customsAndImmigrationMarker,
    [divingSpotMarker.standardMarkerOptions.markerType]: divingSpotMarker,
    [electricalStoreMarker.standardMarkerOptions.markerType]:
      electricalStoreMarker,
    [fishingLocationMarker.standardMarkerOptions.markerType]:
      fishingLocationMarker,
    [fuelDockMarker.standardMarkerOptions.markerType]: fuelDockMarker,
    [fuelStationMarker.standardMarkerOptions.markerType]: fuelStationMarker,
    [generalInformationMarker.standardMarkerOptions.markerType]:
      generalInformationMarker,
    [groceryStoreMarker.standardMarkerOptions.markerType]: groceryStoreMarker,
    [hardwareStoreMarker.standardMarkerOptions.markerType]: hardwareStoreMarker,
    [hauloutFacilityMarker.standardMarkerOptions.markerType]:
      hauloutFacilityMarker,
    [incidentMarker.standardMarkerOptions.markerType]: incidentMarker,
    [lighthouseMarker.standardMarkerOptions.markerType]: lighthouseMarker,
    [marinaMarker.standardMarkerOptions.markerType]: marinaMarker,
    [marineInformationMarker.standardMarkerOptions.markerType]:
      marineInformationMarker,
    [marineLifeMarker.standardMarkerOptions.markerType]: marineLifeMarker,
    [marpMarker.standardMarkerOptions.markerType]: marpMarker,
    [mooringMarker.standardMarkerOptions.markerType]: mooringMarker,
    [navigationInformationMarker.standardMarkerOptions.markerType]:
      navigationInformationMarker,
    [needHelpMarker.standardMarkerOptions.markerType]: needHelpMarker,
    [overheadCableMarker.standardMarkerOptions.markerType]: overheadCableMarker,
    [plumbingStoreMarker.standardMarkerOptions.markerType]: plumbingStoreMarker,
    [policeMarker.standardMarkerOptions.markerType]: policeMarker,
    [portOverviewMarker.standardMarkerOptions.markerType]: portOverviewMarker,
    [pumpOutMarker.standardMarkerOptions.markerType]: pumpOutMarker,
    [showerMarker.standardMarkerOptions.markerType]: showerMarker,
    [snorkelingSpotMarker.standardMarkerOptions.markerType]:
      snorkelingSpotMarker,
    [toiletsMarker.standardMarkerOptions.markerType]: toiletsMarker,
    [unsafeAnchorageMarker.standardMarkerOptions.markerType]:
      unsafeAnchorageMarker,
    [warningMarker.standardMarkerOptions.markerType]: warningMarker,
    [webcamMarker.standardMarkerOptions.markerType]: webcamMarker,
    [wharfMarker.standardMarkerOptions.markerType]: wharfMarker,
  };

  if (hasFeatureEnabled('deckee-signal')) {
    configuration.signalMarkers = signalMarkers;
  }

  configuration.marketplaceMarkers = marketplaceMarkers;
  configuration.tempLocationMarker = tempLocationMarker;
  configuration.currentLocationMarker = currentLocationMarker;

  // LEAVING THIS HERE FOR REFERENCE FOR FUTURE FEATURES
  // Features can be updated here.
  // if (hasFeatureEnabled('gov-au-wa-2020-07')) {
  //   configuration.activityAreaWA.menuOptions.visible = true;
  //   configuration.restrictedAreaWA.menuOptions.visible = true;
  //   configuration.speedLimitationAreaWA.menuOptions.visible = true;
  // }

  // After requesting the layersDefinition, we should always check the configuration is valid
  // in case we've added or removed layers due to features.
  // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
  checkConfigurationAndUpdateIfRequiredDebounced(configuration);

  return configuration;
};

export const buildMapLayerSettingsObj = () => {
  // Previously we stored selected layers as an array of strings, which only included layers that were
  // selected. This made it difficult to introduce default states to new layers, as we couldn't track
  // if a layer had been turned off by the user, or if it was off by default

  const selectedLayers = getStatic('mapLayerService', 'selectedMapLayers');
  const isNewUser = !selectedLayers;

  let nextSelectedLayers = {};
  // If `selectedLayers` is an array, it means that it's still in the old format. We need to upgrade
  // it to be an object with a boolean value of whether the layer is on or off
  if (isSelectedLayersInOldFormat()) {
    nextSelectedLayers = selectedLayers.reduce(
      (acc, layerName) => ({
        ...acc,
        [layerName]: true,
      }),
      {},
    );
  } else {
    nextSelectedLayers = selectedLayers || {};
  }

  // We are unable to determine if layers introduced before this change were turned off by the user
  // or not. So we should assume that any layer marked as an older layer should just be off,
  // because that is what state the user would have seen it in.
  const allLayers = getLayerDefinitions();
  Object.keys(allLayers).forEach((layerName) => {
    const layerConfig = allLayers[layerName];

    // If the layer already exists in the `nextSelectedLayers` object then just skip
    if (
      nextSelectedLayers[layerName] === true ||
      nextSelectedLayers[layerName] === false
    )
      return;

    // If the user is a new user then skip as we want to use the system defaults. Otherwise, if the layer
    // is marked as an older layer and hasn't been set already, then we assume it was off previouslt
    if (!isNewUser && OLD_MARKERS.includes(layerName)) {
      nextSelectedLayers[layerName] = false;
    } else {
      // Anything is shownByDefault and doesn't exist on a menu is always on.
      const isAlwaysOnLayers =
        !layerConfig.menuOptions || !layerConfig.menuOptions.visible;

      // The layer is a "newer" layer, and should just use the default configuration in the layer config. If there isn't a default value, then assume it is `true`
      nextSelectedLayers[layerName] =
        isAlwaysOnLayers || (layerConfig.shownByDefault ?? true);
    }
  });

  return nextSelectedLayers;
};

const checkConfigurationAndUpdateIfRequired = async (
  layerDefinitions,
  forceUpdate = false,
) => {
  const mapLayerSettings = buildMapLayerSettingsObj();

  telemetry(
    telemetry.DEBUG,
    `[map-layer.checkConfigurationAndUpdateIfRequired] filtered selectedMapLayers is ${JSON.stringify(
      mapLayerSettings,
    )}`,
  );

  const storeSettings = getSelectedLayers(getStore().getState());

  // Check to see if we need to update both the store and local storage
  if (forceUpdate || deepEqual(mapLayerSettings, storeSettings)) {
    getStore().dispatch(updateSelectedMapLayers(mapLayerSettings));
  } else {
    telemetry(
      telemetry.DEBUG,
      '[map-layer.checkConfigurationAndUpdateIfRequired] current config is valid',
    );
  }
};

const checkConfigurationAndUpdateIfRequiredDebounced = debounce(
  // eslint-disable-next-line no-use-before-define
  checkConfigurationAndUpdateIfRequired,
  5000,
);

export const initialize = async () => {
  // eslint-disable-next-line no-warning-comments
  // TODO: remove these v3 migrations
  const { selectedLayerTypes: oldSelectedLayerTypes } =
    (await get('map', 'settings')) || {};
  const newSelectedLayerTypes = await get(
    'mapLayerService',
    'selectedMapLayers',
  );
  if (oldSelectedLayerTypes) {
    telemetry(
      telemetry.DEBUG,
      `[mapLayerService] found old selectedLayerTypes '${oldSelectedLayerTypes}'`,
    );
    if (newSelectedLayerTypes) {
      telemetry(
        telemetry.DEBUG,
        `[mapLayerService] found new newSelectedLayerTypes '${newSelectedLayerTypes}'`,
      );
    } else {
      telemetry(
        telemetry.DEBUG,
        `[mapLayerService] setting selectedMapLayers to oldSelectedLayerTypes '${oldSelectedLayerTypes}'`,
      );
      // Ignore this, just redo the current selection. This was causing no layers to be set due
      // to the layer names having heavily changed.
      // set('mapLayerService', 'selectedMapLayers', oldSelectedLayerTypes);
    }
    telemetry(
      telemetry.DEBUG,
      `[mapLayerService] removing oldSelectedLayerTypes '${oldSelectedLayerTypes}'`,
    );
    set('map', 'settings', {
      ...get('map', 'settings'),
      selectedLayerTypes: undefined,
    });
    // Removal of old 'map, 'settings' storage is handled by mapStyleService
  }
  // end v3 migrations

  const initialConfiguration = getLayerDefinitions();
  await checkConfigurationAndUpdateIfRequired(initialConfiguration, true);

  // Test configurations
  // eslint-disable-next-line no-warning-comments
  // TODO: Put all the following in unit testing once we've got some up and running.
  const issues = {};

  // Configuration checks
  Object.keys(initialConfiguration).forEach((name) => {
    /* eslint-disable max-len */
    // The format for each of these entries is {
    //   displayName: string; // The display name for this layer for the front-end
    //   synonyms: '', // Allows for us to match possible user input to a layer
    //   group: string; // This allows us to assign grouped functionality to different classifications of layers
    //   // Settings
    //   shownByDefault: boolean; // Whether this layer is on initially by default
    //   relativeDepth: number; // How to order this layer relative to others. The larger, the deeper, or more 'behind' other layers
    //   // Events
    //   onOnInitialize(map: Map): void; // Code to run on initialization of the map
    //   onToggle(map: Map, show: Boolean): void; // : Code to run when a layer is toggled on or off
    //   onMapUpdateEvent?(map: Map): void; // When the map view is updated (moveend event). This is debounced prior to calling.
    //   // Configuration functions
    //   standardMarkerOptions: StandardMarker; // Configurations pertaining to just standard marker behaviors
    //   menuOptions: MenuOptions; // Configurations for how this layer is displayed in menu
    //   icon: IconOptions; // Configurations for how this layers icons look
    //   popoverConfiguration?(marker): {}; // Function that gives the popover configuration if this layer creates a popover
    // }
    // StandardMarker {
    //   markerType: string; // The entity on the backend that this layer corresponds to.
    //   allowCreation: boolean; // Whether users can edit a given marker
    //   allowEditing: boolean; // Whether the marker has editing ability
    //   allowComments: boolean; // Whether the marker has comments enabled
    // }
    // MenuOptions {
    //   visible: boolean; // If the menu item for this layer is shown
    //   owningMenu: string; // The menu where this layer option should be shown
    //   order: number; // The relative order to be shown, the lower the number the earlier / higher the item is shown
    // }
    // IconOptions {
    //   baseShape: Promise; // A promise that returns the configuration for the marker
    //   iconColor: string; // The foreground color to use for this layers icons
    //   markerColor: string; // The background color to use for this layers icons
    // }
    /* eslint-enable max-len */

    const missing = [];
    let extras = [];

    const check = (data, prefix, setToChe) => {
      const keys = new Set(Object.keys(data));
      Object.keys(setToChe).forEach((property) => {
        if (setToChe[property]) {
          // required
          if (!keys.has(property)) {
            missing.push(`${prefix}.${property}`);
          } else {
            keys.delete(property);
          }
        } else if (keys.has(property)) {
          // Not required
          keys.delete(property);
        }
      });
      extras = [...extras, ...[...keys].map((i) => `${prefix}.${i}`)];
    };

    check(initialConfiguration[name], 'root', {
      // Required
      displayName: true,
      synonyms: true,
      group: true,
      typeUrl: true,
      icon: true,
      shownByDefault: true,
      relativeDepth: true,
      onInitialize: true,
      menuOptions: true,
      // Optional
      standardMarkerOptions: false,
      onToggle: false,
      onMapUpdateEvent: false,
      markerSummaryConfig: false,
      popOverConfiguration: false,
      shareText: false,
    });

    if (initialConfiguration[name].menuOptions) {
      check(initialConfiguration[name].menuOptions || {}, 'root.menuOptions', {
        // Required
        visible: true,
        owningMenu: false,
        order: false,
      });
    }

    if (initialConfiguration[name].icon) {
      check(initialConfiguration[name].icon || {}, 'root.icon', {
        // Required
        baseShape: true,
        iconColor: false,
        markerColor: false,
      });
    }

    if (initialConfiguration[name].standardMarkerOptions) {
      check(
        initialConfiguration[name].standardMarkerOptions || {},
        'root.standardMarkerOptions',
        {
          // Required
          markerType: true,
          allowCreation: true,
          allowEditing: true,
          allowComments: true,
          viewOptions: true,
          creationOptions: true,
          editingOptions: true,
        },
      );
      check(
        initialConfiguration[name].standardMarkerOptions.creationOptions || {},
        'root.standardMarkerOptions.creationOptions',
        {
          // Required
          menuText: true,
          newTitle: true,
          titlePlaceholder: true,
          markerTypeDescription: true,
          checkBoxItems: false,
        },
      );
      check(
        initialConfiguration[name].standardMarkerOptions.viewOptions || {},
        'root.standardMarkerOptions.viewOptions',
        {
          // Required
          typeTitle: true,
          itemTitleFunction: true,
        },
      );
      check(
        initialConfiguration[name].standardMarkerOptions.editingOptions || {},
        'root.standardMarkerOptions.editingOptions',
        {
          // Required
        },
      );
    }

    if (missing.length || extras.length) {
      issues[name] = {
        missing,
        extras,
      };
    }
  });
  if (Object.keys(issues).length) {
    /* eslint-disable-next-line no-console */
    console.error(`Invalid layer definitions for ${Object.keys(issues)}.`);
    /* eslint-disable-next-line no-console */
    console.error(JSON.stringify(issues));
  }
};
